import { Ticket } from "lucide-react";
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { customerApi } from "../../api/client";
import EmptyState from "../../components/common/EmptyState";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import LoadingState from "../../components/common/LoadingState";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import type { Order } from "../../types/api";
import { formatCurrency, formatDateTime, formatEventDate } from "../../utils/format";

function getTicketTone(status: Order["tickets"][number]["status"]) {
  if (status === "checked-in") {
    return "bg-emerald-50 text-emerald-700";
  }

  if (status === "cancelled") {
    return "bg-red-50 text-red-600";
  }

  return "bg-amber-50 text-amber-700";
}

export default function TicketLookupPage() {
  const { customer, token, signOut } = useCustomerAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Your session expired. Please sign in again.");
      setIsLoading(false);
      return;
    }

    let active = true;

    const loadOrders = async (withLoading = false) => {
      if (withLoading) {
        setIsLoading(true);
      }

      try {
        const { orders: savedOrders } = await customerApi.myOrders(token);
        if (active) {
          setOrders(savedOrders);
          setError(null);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError instanceof Error ? requestError.message : "Could not load your saved tickets");
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void loadOrders(true);
    const intervalId = window.setInterval(() => void loadOrders(false), 15000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [token]);

  return (
    <div className="aan-ticket-flow min-h-screen bg-black text-white">
      <Navbar />

      <section className="pb-24 pt-36">
        <div className="section-frame">
          <div className="surface-card mb-8 p-6 sm:p-8">
            <div className="mb-3 font-display text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">AAN Tickets</div>
            <h1 className="font-display text-5xl font-black uppercase leading-none text-foreground">Your saved tickets.</h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-muted-foreground">
              Tickets bought while signed in stay attached to your African Art Network account.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[24px] border border-line bg-surface-muted px-5 py-4 text-sm text-muted-foreground">
              <div>
                Signed in as <span className="font-semibold text-foreground">{customer?.email}</span>
              </div>
              <button
                type="button"
                onClick={signOut}
                className="rounded-full bg-white px-4 py-2 font-display text-xs font-black uppercase text-black hover:bg-aan-bright"
              >
                Sign out
              </button>
            </div>
          </div>

          {isLoading ? (
            <LoadingState label="Loading your saved tickets..." />
          ) : error ? (
            <div className="surface-card px-6 py-12 text-center text-sm font-semibold text-red-500">{error}</div>
          ) : orders.length === 0 ? (
            <EmptyState
              title="No saved tickets yet"
              description="Buy an event ticket while signed in and it will appear here automatically."
            />
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order.reference} className="surface-card overflow-hidden p-6 sm:p-8">
                  <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
                    <div>
                      <div className="mb-3 flex flex-wrap items-center gap-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                          Order reference
                        </div>
                        <div className="rounded-full bg-surface-muted px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                          {(order.tickets ?? []).filter((ticket) => ticket.status === "checked-in").length}/
                          {(order.tickets ?? []).length} checked in
                        </div>
                        <div className="rounded-full bg-surface px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                          Payment {order.paymentStatus}
                        </div>
                      </div>
                      <h2 className="font-display text-4xl font-black uppercase leading-none text-foreground">{order.reference}</h2>
                      <p className="mt-3 text-sm text-muted-foreground">
                        {order.event.title} - {formatEventDate(order.event.date, order.event.time)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {order.event.venue}, {order.event.city}
                      </p>

                      <div className="mt-6 grid gap-5 md:grid-cols-2">
                        {order.tickets.map((ticketItem) => (
                          <div key={ticketItem.code} className="rounded-[8px] border border-line bg-surface-muted p-5">
                            <div className="mb-4 flex items-center justify-between">
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                                  {ticketItem.ticketName}
                                </div>
                                <div className="mt-1 text-lg font-bold text-foreground">{ticketItem.attendeeName}</div>
                              </div>
                              <div
                                className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] ${getTicketTone(ticketItem.status)}`}
                              >
                                {ticketItem.status}
                              </div>
                            </div>
                            <div className="rounded-[8px] bg-white p-4">
                              <QRCodeSVG value={ticketItem.payload} size={200} className="mx-auto" />
                            </div>
                            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                              Code
                            </div>
                            <div className="mt-1 text-sm font-semibold text-foreground">{ticketItem.code}</div>
                            {ticketItem.checkedInAt && (
                              <div className="mt-3 text-sm font-medium text-emerald-700">
                                Verified on {formatDateTime(ticketItem.checkedInAt)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <aside className="space-y-4">
                      <div className="rounded-[8px] border border-white/15 bg-black p-6 text-white">
                        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">
                          <Ticket size={14} />
                          Payment summary
                        </div>
                        <div className="space-y-3 text-sm text-slate-300">
                          <div className="flex items-center justify-between">
                            <span>Subtotal</span>
                            <span>{formatCurrency(order.subtotal)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Fees</span>
                            <span>{formatCurrency(order.serviceFee)}</span>
                          </div>
                          <div className="flex items-center justify-between border-t border-white/10 pt-3 text-lg font-bold text-white">
                            <span>Total</span>
                            <span>{formatCurrency(order.total)}</span>
                          </div>
                        </div>
                      </div>
                    </aside>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
